import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import { Xmark } from 'iconoir-react';
import { Button, ConfirmationModal, Heading } from '@knowtex-ai/components';
import { useTutorial } from './useTutorial';
import { renderContent } from './utils/tooltipContentUtils';
import { 
  findElementWithRetry, 
  isElementVisible, 
  performAutoAction as performAutoActionUtil,
} from './utils/tooltipElementUtils';
import { calculateSpotlightPosition, spotlightTarget } from './utils/tooltipPositionUtils';
import {
  REVIEW_SMART_ASSIST_STEP_ID,
  SMART_ASSIST_STEP_IDS,
  SMART_CHART_SMART_ASSIST_STEP_IDS,
} from './utils/smartAssistSlides';
import { useTooltipFloating } from './hooks/useTooltipFloating';
import { 
  handleNextTabNavigation, 
  handlePreviousTabNavigation 
} from './utils/tooltipTabUtils';
import {
  MODAL_NUDGE_STEPS,
  NON_BLOCKING_BACKDROP_STEPS,
  SPOTLIGHT_ONLY_DIM_STEPS,
  TAB_ARIA_LABEL_BY_STEP,
  TOP_RIGHT_STEPS,
  POSITIONING,
  STEP_IDS,
} from './utils/tooltipConstants';
import { getSessionToken } from "../../lib/sessionToken";

// While the tutorial runs, everything but the highlighted control is taken out
// of hit-testing through this class and stylesheet.
const INTERACTION_LOCK_CLASS = 'tutorial-lock';
const INTERACTION_LOCK_STYLE_ID = 'tutorial-interaction-lock';
// The tutorial's "this is clickable" highlight (index.css sets pointer-events:
// auto on these). Several slides paint it on an element the step's
// targetSelector never names — the review-note tabs and the settings "My Dot
// Phrases" tab — so the lock has to honour the class, not just the selector.
const CLICKABLE_HIGHLIGHT_SELECTORS = [
  '.tutorial-highlight-flash',
  '.tutorial-highlight-flash-purple',
  '.tutorial-highlight-flash-white',
  '.tutorial-highlight-tab',
];


const REVIEW_TAB_SELECTORS = Object.values(TAB_ARIA_LABEL_BY_STEP)
  .map((label) => `[aria-label="${label}"]`)
  .join(', ');
const SETTINGS_TAB_LABEL = 'My Dot Phrases';

// The tab each of these slides highlights. Resolved by accessible name, because
// the step's targetSelector names the pane, not the tab.
const findHighlightedTab = (stepId) => {
  const ariaLabel = TAB_ARIA_LABEL_BY_STEP[stepId];
  if (ariaLabel) {
    return document.querySelector(`.modal.show [aria-label="${ariaLabel}"]`);
  }
  if (stepId === 'manage_dot_phrase') {
    const wrapper = document.querySelector('#manage_dot_phrase_button');
    if (!wrapper) return null;
    return (
      Array.from(wrapper.querySelectorAll('button')).find(
        (btn) => (btn.textContent || '').trim() === SETTINGS_TAB_LABEL
      ) || null
    );
  }
  return null;
};

const releaseInteractionLock = () => {
  document.body.classList.remove(INTERACTION_LOCK_CLASS);
  document.getElementById(INTERACTION_LOCK_STYLE_ID)?.remove();
};

const TUTORIAL_MODAL_NUDGE_CLASS = 'tutorial-modal-nudge';

const TutorialTooltip = ({
  sectionId,
  stepId,
  stepIndex,
  name,
  content,
  targetSelector,
  position = 'bottom',
  action = 'view'
}) => {
  const { 
    nextStep, 
    previousStep, 
    exitTutorial,
    completeTutorial,
    totalSteps,
    isFirstStep,
    isLastStep,
    progress,
    mode,
    flattenedSteps,
    isActive,
    isFullTutorial,
    patientDescription
  } = useTutorial();
  
  const navigate = useNavigate();
  const [targetElement, setTargetElement] = useState(null);
  const [positionReference, setPositionReference] = useState(null);
  const [tooltipOpacity, setTooltipOpacity] = useState(1);
  const [spotlightPosition, setSpotlightPosition] = useState(null);
  const [tooltipElement, setTooltipElement] = useState(null);
  const clickHandlerRef = useRef(null);
  const previousTargetRef = useRef(null);
  const spotlightUpdateRef = useRef(null);
  const nextStepPendingRef = useRef(false);
  const globalClickBlockerRef = useRef(null);
  const clickBlockerRef = useRef(null);
  const tooltipRef = useRef(null);
  const autoActionPerformedRef = useRef(false);
  const setTooltipRef = useCallback((node) => {
    tooltipRef.current = node;
    setTooltipElement(node);
  }, []);
  const reviewNoteAdvancedRef = useRef(false);
  const addVeteranModalAdvancedRef = useRef(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const isFullTutorialFlow = isFullTutorial;
  const isFeatureSubmitStep = stepId === 'submit_recording' && !isFullTutorialFlow;
  // Every slide shows a footer button. Steps that normally advance when the user
  // clicks the highlighted element are handled in handleNext by simulating that click.
  const showFooterPrimaryButton = true;
  const shouldShowFinishButton = isFeatureSubmitStep || (isLastStep && stepId !== 'add_dot_phrase_in_note');
  // These steps are shown as a centered modal in the middle of the screen.
  const isCenteredModalStep = stepId === 'veteran_consent' || stepId === 'speaking_freely';
  // The "Generating Your Note" step keeps its Next button disabled until the
  // note finishes processing (the Review-status card appears on the dashboard).
  const [isNoteReadyForReview, setIsNoteReadyForReview] = useState(false);
  const isNextDisabled = stepId === 'await_note_ready' && !isNoteReadyForReview;
  const handleFinish = () => {
    if (stepId === 'tutorial_complete' && mode === 'onboarding') {
      sendTutorialSlackNotification('PROVIDER_COMPLETED_TUTORIAL');
    }
    completeTutorial();
  };

  const isCenteredStep = isCenteredModalStep || stepId === 'telehealth_close_modal';

  useEffect(() => {
    // Steps that anchor their card to something other than their target: a
    // specific control, or the window the target sits in when the card should
    // clear the whole thing rather than overlap it.
    const ANCHOR_SELECTORS = {
      return_home: '#return_home',
      review_note_page: '.modal.show [aria-label="Note tab"]',
      transcript_page: '.modal.show [aria-label="Transcript tab"]',
      note_citations: '.modal.show [aria-label="Note citations tab"]',
      patient_visit_summary_page: '.modal.show [aria-label="Patient visit summary tab"]',
      precharting_overview: '[data-tutorial="prechart_field_list"]',
      precharting_custom_instructions: '#precharting_modal_dialog',
    };
    const anchorSelector = ANCHOR_SELECTORS[stepId];
    // Anchor only to an element that is actually laid out. A detached or
    // zero-size node still satisfies Floating UI, which then resolves it to
    // 0,0 and pins the card to the top-left corner; leaving the reference
    // empty instead falls back to the centred card until the target is ready.
    const isUsableAnchor = (el) =>
      Boolean(el) && el.isConnected && el.getBoundingClientRect().width > 0;
    const find = () => {
      const el = anchorSelector ? document.querySelector(anchorSelector) : targetElement;
      setPositionReference(isUsableAnchor(el) ? el : null);
    };
    find();
    // Keep looking: targets that mount or lay out late upgrade the card from
    // centred to anchored as soon as they are on screen.
    const interval = setInterval(find, 200);
    return () => { clearInterval(interval); setPositionReference(null); };
  }, [targetElement, stepId]);

  // positionReference is only ever a laid-out element or null, so this is what
  // separates an anchored card from the centred fallback.
  const hasPositionAnchor = positionReference != null;

  const { floatingStyles } = useTooltipFloating({
    referenceElement: positionReference,
    floatingElement: tooltipElement,
    position,
    stepId,
    enabled: !isCenteredStep && hasPositionAnchor,
  });

  const performAutoAction = () => {
    // Only perform action if action is not null and performAutoActionUtil exists
    if (action !== null && action !== undefined && typeof performAutoActionUtil === 'function') {
      performAutoActionUtil(action, targetSelector);
    }
  };

  useEffect(() => {
    if (!action?.autoClick) {
      autoActionPerformedRef.current = false;
      return;
    }

    if (autoActionPerformedRef.current) {
      return;
    }

    autoActionPerformedRef.current = true;
    const timeoutId = setTimeout(() => {
      performAutoAction();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [action, targetSelector]);

  // On the "Choosing Chart Data" slide, mark the Smart Chart data-object list
  // (Vitals, Labs, Medication List, Allergies, Active Problems) as the
  // clickable target. The rows keep their own click handlers so the user can
  // still toggle selections. Also record the list's right border so the card
  // can sit directly beside it.
  useEffect(() => {
    if (stepId !== 'precharting_overview') return undefined;

    const highlighted = new Set();
    let attempts = 0;
    const maxAttempts = 50;
    const intervalId = setInterval(() => {
      const fieldList = document.querySelector('[data-tutorial="prechart_field_list"]');
      if (fieldList) {
        fieldList.classList.add('tutorial-highlight-flash');
        highlighted.add(fieldList);
        clearInterval(intervalId);
      } else if (++attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, 200);

    return () => {
      clearInterval(intervalId);
      highlighted.forEach((el) => el.classList.remove('tutorial-highlight-flash'));
    };
  }, [stepId]);

  // On the "Writing Custom Instructions" slide, align the card's left border
  // with the Smart Chart window's right border — the same placement the
  // Conversation-driven updates slide gets by anchoring to
  // #precharting_modal_dialog with position "right".


  // Review-note modal steps: the slide's tab (Note / Transcript / Note
  // Citations / Patient Visit Summary) is what the spotlight frames, so it
  // keeps its own colours while the dim falls on everything else. These steps
  // are TOP_RIGHT_STEPS, which never resolve a target element, so the cut-out
  // is measured here rather than by the shared spotlight machinery. Tracked on
  // an interval because the modal animates in and the tab row scrolls, and
  // because the element-search effect clears highlight classes on step change.
  useEffect(() => {
    if (!TAB_ARIA_LABEL_BY_STEP[stepId]) return undefined;

    const track = () => {
      const btn = findHighlightedTab(stepId);
      const rect = btn ? btn.getBoundingClientRect() : null;
      // Drop the cut-out while the tab is missing, hidden, or scrolled out of
      // its row, so it never frames unrelated UI.
      if (
        !rect ||
        rect.width === 0 ||
        rect.height === 0 ||
        rect.bottom < 0 ||
        rect.top > window.innerHeight
      ) {
        setSpotlightPosition(null);
        return;
      }
      if (!btn.classList.contains('tutorial-highlight-tab')) {
        btn.classList.add('tutorial-highlight-tab');
      }
      const next = calculateSpotlightPosition(btn);
      setSpotlightPosition((prev) =>
        prev &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.height === next.height
          ? prev
          : next
      );
    };
    track();
    const intervalId = setInterval(track, 200);

    return () => {
      clearInterval(intervalId);
      setSpotlightPosition(null);
      document.querySelectorAll(REVIEW_TAB_SELECTORS).forEach((el) => {
        el.classList.remove('tutorial-highlight-tab', 'tutorial-highlight-flash-purple');
        el.style.backgroundColor = '';
      });
    };
  }, [stepId]);

  // "My Dot Phrases" step: the tab keeps its own colours and is framed by the
  // spotlight, like the review-note tabs. It stays marked as clickable so the
  // user can open it. The element-search effect clears highlight classes on
  // step change, so keep re-asserting on an interval to survive that sweep.
  useEffect(() => {
    if (stepId !== 'manage_dot_phrase') return undefined;
    const ensureHighlight = () => {
      const tab = findHighlightedTab(stepId);
      if (!tab) return;
      if (!tab.classList.contains('tutorial-highlight-tab')) {
        tab.classList.add('tutorial-highlight-tab');
      }
    };
    ensureHighlight();
    const intervalId = setInterval(ensureHighlight, 250);
    return () => {
      clearInterval(intervalId);
      const tab = findHighlightedTab(stepId);
      if (tab) {
        tab.classList.remove('tutorial-highlight-tab', 'tutorial-highlight-flash-purple');
        tab.style.backgroundColor = '';
        tab.style.color = '';
      }
    };
  }, [stepId]);

  // "Review Note" step: keep the Review button (#review_note) marked as the
  // clickable target. The element-search sweep clears highlight classes on
  // re-render, so re-assert.
  useEffect(() => {
    if (stepId !== 'review_note') return undefined;
    const ensureHighlight = () => {
      const btn = document.querySelector('#review_note');
      if (btn && !btn.classList.contains('tutorial-highlight-flash-white')) {
        btn.classList.add('tutorial-highlight-flash-white');
      }
    };
    ensureHighlight();
    const intervalId = setInterval(ensureHighlight, 250);
    return () => {
      clearInterval(intervalId);
      const btn = document.querySelector('#review_note');
      if (btn) btn.classList.remove('tutorial-highlight-flash-white');
    };
  }, [stepId]);

  // The launch site slide points inside the Select launch site window, so keep
  // it open for exactly that slide: arriving by Next or Previous opens it, and
  // leaving it any way, including exiting the tour, closes it again.
  useEffect(() => {
    if (stepId !== 'select_launch_site') return undefined;
    window.dispatchEvent(new Event('tutorial-open-point-of-care'));
    return () => window.dispatchEvent(new Event('tutorial-close-point-of-care'));
  }, [stepId]);

  // Same for the Smart Assist slides in the Smart Chart window: bring the
  // Smart Assist panel up for the run of them, and put the Smart Chart tab back
  // once the tour moves on. Keyed on the run, not the slide, so stepping
  // between them doesn't flip tabs.
  const isSmartChartSmartAssistStep = SMART_CHART_SMART_ASSIST_STEP_IDS.includes(stepId);
  useEffect(() => {
    if (!isSmartChartSmartAssistStep) return undefined;
    window.dispatchEvent(new Event('tutorial-show-smart-assist'));
    return () => window.dispatchEvent(new Event('tutorial-hide-smart-assist'));
  }, [isSmartChartSmartAssistStep]);

  // The "Ready to Record" slide runs on the dashboard: make sure the Smart
  // Chart window is closed before highlighting the patient card.
  useEffect(() => {
    if (stepId !== 'ready_to_record') return;
    if (document.querySelector('#precharting_modal_dialog')) {
      document.querySelector('#precharting_modal_close')?.click();
    }
  }, [stepId]);

  // The test patient is appended at the bottom of the schedule, so the
  // "Ready to Record" button is usually off-screen. Scroll it into view.
  useEffect(() => {
    if (stepId !== 'ready_to_record') return undefined;
    let attempts = 0;
    const maxAttempts = 40;
    const scrollToButton = () => {
      const btn = document.querySelector('#ready_to_record_button');
      if (btn) {
        btn.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        return true;
      }
      return false;
    };
    if (scrollToButton()) return undefined;
    const intervalId = setInterval(() => {
      if (scrollToButton() || ++attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, 200);
    return () => clearInterval(intervalId);
  }, [stepId]);

  // When the tooltip would overlap a centered page modal, nudge the modal to
  // the right so both are visible side-by-side.
  useEffect(() => {
    if (MODAL_NUDGE_STEPS.has(stepId)) {
      document.body.classList.add(TUTORIAL_MODAL_NUDGE_CLASS);
    } else {
      document.body.classList.remove(TUTORIAL_MODAL_NUDGE_CLASS);
    }
    return () => document.body.classList.remove(TUTORIAL_MODAL_NUDGE_CLASS);
  }, [stepId]);

  // Poll for the note to become ready for review. The dashboard renders the
  // Review-status card (#review_note) once processing finishes, which is what
  // enables the Next button on the "Generating Your Note" step.
  useEffect(() => {
    if (stepId !== 'await_note_ready') {
      setIsNoteReadyForReview(false);
      return undefined;
    }
    const isReady = () => Boolean(document.querySelector('#review_note'));
    if (isReady()) {
      setIsNoteReadyForReview(true);
      return undefined;
    }
    setIsNoteReadyForReview(false);
    const intervalId = setInterval(() => {
      if (isReady()) {
        setIsNoteReadyForReview(true);
        clearInterval(intervalId);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [stepId]);

  // The test patient is appended to the bottom of the schedule. On the Knowtex
  // Smart Chart slide, scroll its Smart Chart button fully into view (scrolling
  // past any patients above it) so the highlight and the Next-button click both
  // land on the test patient's button.
  useEffect(() => {
    if (stepId !== 'precharting_intro') return undefined;
    let attempts = 0;
    const maxAttempts = 40;
    const scrollToButton = () => {
      const btn = document.querySelector('#prechart_button');
      if (btn) {
        btn.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        return true;
      }
      return false;
    };
    if (scrollToButton()) return undefined;
    const intervalId = setInterval(() => {
      if (scrollToButton() || ++attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, 200);
    return () => clearInterval(intervalId);
  }, [stepId]);


  // A second Next press while a delayed advance is pending would advance
  // twice and skip a slide.
  const advanceAfter = (delay) => {
    nextStepPendingRef.current = true;
    setTimeout(() => {
      nextStepPendingRef.current = false;
      nextStep();
    }, delay);
  };

  const handleNext = () => {
    if (nextStepPendingRef.current) {
      return;
    }
    try {
      // Steps that normally advance when the user clicks the highlighted element
      // (open a modal, start/stop recording, navigate, save). The Next button
      // simulates that click so the app flow stays in sync with the tutorial.
      const clickToAdvanceSteps = [
        'add_veteran',
        'precharting_intro',
        'veteran_consent',
        'start_recording',
        'stop_button',
        'submit_recording',
        'return_home',
        'review_note',
        'manage_dot_phrase',
        'save_upload_note',
        'telehealth_setup',
      ];
      if (clickToAdvanceSteps.includes(stepId) && !isFeatureSubmitStep) {
        const anchorElement = targetElement || (targetSelector && document.querySelector(targetSelector));
        if (anchorElement) {
          // veteran_consent anchors to the Ready to Record wrapper (mic + link);
          // click the link inside it so navigation to the record page fires.
          const elementToClick =
            stepId === 'veteran_consent'
              ? anchorElement.querySelector('[data-testid="record-link"]') || anchorElement
              : anchorElement;
          elementToClick.click();
          return;
        }
      }

      if (stepId === 'add_veteran_modal_instructions') {
        if (addVeteranModalAdvancedRef.current) return;
        addVeteranModalAdvancedRef.current = true;
        // Create the test patient (QQQVAINTERFACE,IFC TWO) so the rest of the
        // tutorial has a patient to work with. This also closes the modal.
        window.dispatchEvent(new Event('tutorial-add-test-patient'));
        setTimeout(() => {
          nextStep();
        }, 600);
        return;
      }

      // Leaving the Custom prompt slide: switch the Smart Chart window to the
      // Custom Prompt tab so the next slide can point at the Optional Outline
      // button inside it.
      if (stepId === 'precharting_custom_prompt') {
        const customPromptTab = document.querySelector('#custom-prompt-tab');
        customPromptTab?.click();
        advanceAfter(400);
        return;
      }

      // The Smart Chart window stays open through the Smart Assist slides, so
      // close it on the way into "Ready to Record", which anchors to a patient
      // card on the dashboard behind it. Keyed to the next step rather than a
      // slide id because the Smart Assist slides are feature-flagged. The delay
      // lets the dashboard lay out, otherwise the slide measures a card that
      // isn't on screen yet and pins itself to the corner.
      const nextStepId = flattenedSteps?.[(stepIndex ?? 0) + 1]?.stepId;
      if (nextStepId === 'ready_to_record' && document.querySelector('#precharting_modal_dialog')) {
        document.querySelector('#precharting_modal_close')?.click();
        advanceAfter(400);
        return;
      }

      if (stepId === 'tutorial_complete' && mode === 'onboarding') {
        sendTutorialSlackNotification('PROVIDER_COMPLETED_TUTORIAL');
        completeTutorial();
        return;
      }
      
      if (stepId === 'locked_note') {
        if (isFullTutorialFlow) {
          // Navigate first; advance step only after route has changed so the next step
          // (navigate_settings) runs on the settings page and the tutorial doesn't close.
          navigate('/settings');
          const NAVIGATE_THEN_NEXT_DELAY_MS = 800;
          setTimeout(() => {
            nextStep();
          }, NAVIGATE_THEN_NEXT_DELAY_MS);
          return;
        } else {
          nextStep();
          return;
        }
      }
      
      performAutoAction();
      
      const willNavigate = action && typeof action === 'object' && 
         action.targetSelector && 
         (action.targetSelector.includes('record-link') || action.targetSelector.includes('href'));
      
      if (handleNextTabNavigation && typeof handleNextTabNavigation === 'function' && handleNextTabNavigation(sectionId, stepId, nextStep)) {
        return;
      }
      
      if (willNavigate) {
        if (nextStepPendingRef.current) {
          return;
        }
        
        nextStepPendingRef.current = true;
        
        const checkPageLoad = () => {
          const initialPath = window.location.pathname;
          let checkCount = 0;
          const maxChecks = 50;
          
          const checkInterval = setInterval(() => {
            checkCount++;
            const currentPath = window.location.pathname;
            
            if (currentPath !== initialPath && document.readyState === 'complete') {
              clearInterval(checkInterval);
              setTimeout(() => {
                if (nextStepPendingRef.current) {
                  nextStepPendingRef.current = false;
                  nextStep();
                }
              }, 2000);
              return;
            }
            
            if (checkCount >= maxChecks) {
              clearInterval(checkInterval);
              if (nextStepPendingRef.current) {
                nextStepPendingRef.current = false;
                nextStep();
              }
            }
          }, 100);
        };
        
        setTimeout(checkPageLoad, 200);
        return;
      }
      
      // Default delay of 200ms if POSITIONING.ACTION_DELAY is not defined
      const defaultActionDelay = (POSITIONING && POSITIONING.ACTION_DELAY) ? POSITIONING.ACTION_DELAY : 200;
      const delay = stepId === 'ready_to_record' ? 800
        : (stepId === 'speaking_freely' ? 300 
        : (stepId === 'stop_button' ? 1000 
        : (stepId === 'leave_feedback' ? 100
        : (stepId === 'additional_feedback' ? 300
        : (stepId === 'patient_visit_summary_page' ? 0
        : defaultActionDelay)))));
      
      if (nextStepPendingRef.current) {
        return;
      }
      nextStepPendingRef.current = true;
      setTimeout(() => {
        nextStepPendingRef.current = false;
        nextStep();
      }, delay);
    } catch (error) {
      console.error('[Tutorial] Error in handleNext:', error);
      console.error('[Tutorial] stepId:', stepId);
      console.error('[Tutorial] flattenedSteps:', flattenedSteps);
      console.error('[Tutorial] action:', action);
      // Still try to advance to prevent getting stuck
      if (nextStep && typeof nextStep === 'function') {
        nextStep();
      }
    }
  };

  const handlePrevious = () => {
    if (handlePreviousTabNavigation(sectionId, stepId, previousStep)) {
      return;
    }
    
    if (sectionId === 'settings' && stepId === 'manage_dot_phrase') {
      const uploadTabButton = document.querySelector('#add_new_dot_phrase_button');
      if (uploadTabButton) {
        uploadTabButton.click();
        const scrollDelay = (POSITIONING && POSITIONING.SCROLL_DELAY) ? POSITIONING.SCROLL_DELAY : 300;
        setTimeout(() => {
          previousStep();
        }, scrollDelay);
        return;
      }
    }
    
    previousStep();
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('tutorial-backdrop-bg')) {
      handleCloseAttempt();
    }
  };

  useEffect(() => {
    if (stepId === 'add_veteran_modal_instructions') {
      addVeteranModalAdvancedRef.current = false;
    }
  }, [stepId]);

  // Full tutorial only: advance from review_note when the Review modal opens (fallback if button click doesn't fire)
  useEffect(() => {
    if (stepId !== 'review_note') return;
    reviewNoteAdvancedRef.current = false;
    if (!isFullTutorialFlow) return;

    const onModalShown = () => {
      if (reviewNoteAdvancedRef.current) return;
      reviewNoteAdvancedRef.current = true;
      document.removeEventListener('shown.bs.modal', onModalShown, true);
      nextStep();
    };
    document.addEventListener('shown.bs.modal', onModalShown, true);
    return () => document.removeEventListener('shown.bs.modal', onModalShown, true);
  }, [stepId, isFullTutorialFlow, nextStep]);

  const sendTutorialSlackNotification = async (eventType) => {
    try {
      if (!getSessionToken()) {
        return;
      }

      const decoded_token = jwt_decode(getSessionToken());
      const userId = decoded_token.user?.id;
      
      let userName = '';
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/dashboard/userinfo`,
          {
            method: 'GET',
            headers: { token: getSessionToken() },
          }
        );
        const parseRes = await response.json();
        userName = parseRes[0]?.user_name || '';
      } catch (err) {
        console.error('[Tutorial] Error fetching user info:', err.message);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/slack-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            token: getSessionToken(),
          },
          body: JSON.stringify({
            event: eventType,
            userData: {
              userName: userName,
              userId: userId,
            },
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error(`[Tutorial] Slack notification failed with status ${response.status}`);
        console.error('[Tutorial] Error response:', errorData);
      }
    } catch (err) {
      console.error('[Tutorial] Error sending slack notification:', err.message);
      console.error('[Tutorial] Error details:', err);
    }
  };

  const doExit = useCallback(() => {
    if (mode === 'onboarding') {
      sendTutorialSlackNotification('PROVIDER_SKIPPED_TUTORIAL');
    }
    exitTutorial();
  }, [mode, exitTutorial]);

  const handleCloseAttempt = useCallback(() => {
    if ((stepIndex ?? 0) >= 1) {
      setShowExitConfirmation(true);
    } else {
      doExit();
    }
  }, [stepIndex, doExit]);

  // The interaction lock swallows keys aimed at the page, so keyboard and
  // screen reader users need focus inside the slide. Leave it alone when it
  // already sits on something they can use, like the highlighted control.
  useEffect(() => {
    if (!isActive) {
      return;
    }
    const active = document.activeElement;
    const tooltip = tooltipRef.current;
    if (tooltip && (!active || active === document.body || tooltip.contains(active))) {
      tooltip.focus({ preventScroll: true });
    }
  }, [isActive, stepId]);

  useEffect(() => {
    if (targetElement && targetSelector) {
      const spotlightPos = calculateSpotlightPosition(spotlightTarget(stepId, targetElement));
      setSpotlightPosition(spotlightPos);
    } else if (!TAB_ARIA_LABEL_BY_STEP[stepId]) {
      // The review-note tab slides measure their own cut-out and never resolve
      // a target element, so clearing here would just fight that effect.
      setSpotlightPosition(null);
    }

    if (stepId === 'navigate_settings') {
      const modalBackdrop = document.querySelector('.modal-backdrop');
      if (modalBackdrop) {
        modalBackdrop.remove();
      }
      document.body.classList.remove('modal-open');
      const highlightedElements = document.querySelectorAll('.tutorial-highlight, .tutorial-highlight-flash, .tutorial-highlight-grey');
      highlightedElements.forEach(el => {
        el.classList.remove('tutorial-highlight', 'tutorial-highlight-flash', 'tutorial-highlight-grey');
        el.style.zIndex = '';
        el.style.position = '';
      });
    }
  }, [stepId, targetElement, targetSelector]);

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;
    let retryTimeoutId = null;
    
    if (previousTargetRef.current) {
      previousTargetRef.current.classList.remove('tutorial-highlight', 'tutorial-highlight-flash', 'tutorial-highlight-grey');
      previousTargetRef.current.style.zIndex = '';
      previousTargetRef.current.style.position = '';
      if (clickHandlerRef.current) {
        previousTargetRef.current.removeEventListener('click', clickHandlerRef.current);
        clickHandlerRef.current = null;
      }
    }
    
    const allHighlightedElements = document.querySelectorAll('.tutorial-highlight, .tutorial-highlight-flash, .tutorial-highlight-grey');
    allHighlightedElements.forEach((el) => {
      el.classList.remove('tutorial-highlight', 'tutorial-highlight-flash', 'tutorial-highlight-grey');
      el.style.zIndex = '';
      el.style.position = '';
      el.style.backgroundColor = '';
    });
    
    if (sectionId === 'settings') {
      ['#model_type_selector', '#add_new_dot_phrase', '#upload_macros_panel', '#manage_dot_phrase'].forEach((sel) => {
        const el = document.querySelector(sel);
        if (el) {
          el.classList.remove('tutorial-highlight', 'tutorial-highlight-flash', 'tutorial-highlight-grey');
          el.style.zIndex = '';
          el.style.position = '';
          el.style.backgroundColor = '';
        }
      });
    }
    
    
    if (stepId !== 'leave_feedback') {
      const leaveFeedbackElements = document.querySelectorAll('[data-tutorial="leave_feedback"]');
      leaveFeedbackElements.forEach((leaveFeedbackElement) => {
        leaveFeedbackElement.classList.remove('tutorial-highlight', 'tutorial-highlight-flash', 'tutorial-highlight-grey');
        leaveFeedbackElement.style.zIndex = '';
        leaveFeedbackElement.style.position = '';
      });
    }

    if (targetSelector) {
      setTooltipOpacity(0);
    }
    
    if (TOP_RIGHT_STEPS.includes(stepId)) {
      setTargetElement(null);
      setTooltipOpacity(1);
      if (spotlightUpdateRef.current) {
        clearInterval(spotlightUpdateRef.current);
        spotlightUpdateRef.current = null;
      }
      return;
    }
    
    if (!targetSelector || targetSelector === null) {
      setTargetElement(null);
      setTooltipOpacity(1);
      return;
    }
      
    // locked_note is intentionally excluded: the note is locked immediately and
    // the #review_note button is already on screen, so its search must run with
    // no wait for the Locked Note slide to appear right after Save & Upload.
    const needsPageLoadDelay = stepId === 'telehealth_setup' || stepId === 'record_page_intro';
    
    const waitForPageReady = () => {
      return new Promise((resolve) => {
        if (document.readyState === 'complete') {
          resolve();
        } else {
          const checkReady = () => {
            if (document.readyState === 'complete') {
              window.removeEventListener('load', checkReady);
              resolve();
            }
          };
          window.addEventListener('load', checkReady);
          setTimeout(() => {
            window.removeEventListener('load', checkReady);
            resolve();
          }, 100);
        }
      });
    };
    
    const pageLoadDelay = needsPageLoadDelay ? 1500 : 0;
    
    const maxRetries = stepId === 'pause_button' ? 15 
      : (stepId === 'stop_button' ? 5 
      : (stepId === 'submit_recording' ? 20 
      : (stepId === 'telehealth_setup' ? 20 
      : (stepId === 'manage_dot_phrase' ? 40 
      : (stepId === 'locked_note' ? 50 : 10)))));
    const retryDelay = stepId === 'pause_button' ? 100 
      : (stepId === 'stop_button' ? 50 
      : (stepId === 'submit_recording' ? 200 
      : (stepId === 'telehealth_setup' ? 150 
      : (stepId === 'manage_dot_phrase' ? 200 
      : (stepId === 'locked_note' ? 200 : 200)))));
    
    const startElementSearch = async () => {
      if (needsPageLoadDelay) {
        await waitForPageReady();
        await new Promise(resolve => setTimeout(resolve, pageLoadDelay));
      }
      
      if (!isMounted) return;
      
      findElementWithRetry(targetSelector, maxRetries, retryDelay, stepId, {
        isFullTutorial: isFullTutorialFlow,
      }).then((foundElement) => {
        if (!isMounted) return;
        
        if (!foundElement) {
          if (stepId === 'manage_dot_phrase') {
            setTargetElement(null);
            setTooltipOpacity(1);
            
            let additionalAttempts = 0;
            const maxAdditionalRetries = 50;
            const additionalDelay = 200;
            
            const continueRetrying = () => {
              if (!isMounted) return;
              
              const element = document.querySelector(targetSelector);
              if (element) {
                element.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
                
                setTimeout(() => {
                  if (!isMounted) return;
                  
                  const rect = element.getBoundingClientRect();
                  const computedStyle = window.getComputedStyle(element);
                  const isVisible = element.offsetParent !== null && 
                    computedStyle.display !== 'none' &&
                    computedStyle.visibility !== 'hidden' &&
                    rect.width > 0 &&
                    rect.height > 0;
                  
                  if (isVisible) {
                    const foundEl = element;
                    setTargetElement(foundEl);
                    previousTargetRef.current = foundEl;
                    
                    if (!TOP_RIGHT_STEPS.includes(stepId) && stepId !== STEP_IDS.LEAVE_FEEDBACK && !isFeatureSubmitStep) {
                      const informationalSteps = ['schedule_refresh', 'additional_recording', 'na_declined', 'voice_activity_detection', 'leave_feedback'];
                      if (stepId !== 'add_veteran_modal_instructions') {
                        if (informationalSteps.includes(stepId)) {
                          foundEl.classList.add('tutorial-highlight-grey');
                        } else {
                          foundEl.classList.add('tutorial-highlight');
                          const clickableSteps = ['veteran_consent', 'start_recording', 'stop_button', 'submit_recording', 'return_home', 'review_note', 'save_upload_note', 'manage_dot_phrase', 'telehealth_setup'];
                          if (clickableSteps.includes(stepId)) {
                            if (stepId === 'manage_dot_phrase') {
                              setTimeout(() => {
                                foundEl.classList.add('tutorial-highlight-flash');
                              }, 3000);
                          } else if (stepId === 'telehealth_setup' || stepId === 'telehealth_microphone_step' || stepId === 'telehealth_screen_audio_step') {
                              foundEl.classList.add('tutorial-highlight');
                            } else {
                              foundEl.classList.add('tutorial-highlight-flash');
                            }
                          }
                        }
                        if (stepId === 'telehealth_setup') {
                          const labelElement = foundEl.closest('label');
                          if (labelElement) {
                            labelElement.classList.add('tutorial-highlight-flash');
                            labelElement.style.position = 'relative';
                            labelElement.style.zIndex = '10001';
                          }
                        }

                        if (stepId === 'telehealth_microphone_step' || stepId === 'telehealth_screen_audio_step') {
                          foundEl.classList.add('tutorial-highlight');
                          foundEl.style.position = 'relative';
                          foundEl.style.zIndex = '10001';
                        }
                      }
                      foundEl.style.position = 'relative';
                      foundEl.style.zIndex = '10001';
                      if (sectionId === 'settings') {
                        foundEl.style.backgroundColor = 'white';
                      }
                    }
                    
                    if (stepId === 'leave_feedback') {
                      const leaveFeedbackElement = foundEl || document.querySelector(targetSelector);
                      if (leaveFeedbackElement) {
                        leaveFeedbackElement.classList.add('tutorial-highlight');
                        leaveFeedbackElement.style.position = 'relative';
                        leaveFeedbackElement.style.zIndex = '10001';
                      }
                    }

                    
                    const spotlightPos = calculateSpotlightPosition(spotlightTarget(stepId, foundEl));
                    setSpotlightPosition(spotlightPos);
                    if (stepId === 'leave_feedback') {
                      setTimeout(() => {
                        foundEl.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center',
                          inline: 'nearest'
                        });
                      }, 150);
                    }

                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        const newSpotlightPos = calculateSpotlightPosition(spotlightTarget(stepId, foundEl));
                        setSpotlightPosition(newSpotlightPos);
                      });
                    });
                    return;
                  }
                }, 100);
              }
              
              if (additionalAttempts < maxAdditionalRetries) {
                additionalAttempts++;
                setTimeout(continueRetrying, additionalDelay);
              }
            };
            
            setTimeout(continueRetrying, additionalDelay);
            return;
          }
          
          if (stepId === 'submit_recording') {
            setTargetElement(null);
            setTooltipOpacity(1);
            
            let additionalAttempts = 0;
            const maxAdditionalRetries = 30;
            const additionalDelay = 200;
            
            const continueRetrying = () => {
              if (!isMounted) return;
              
              const element = document.querySelector(targetSelector);
              if (element) {
                const foundEl = element;
                  setTargetElement(foundEl);
                  previousTargetRef.current = foundEl;
                
                  if (!TOP_RIGHT_STEPS.includes(stepId) && stepId !== STEP_IDS.LEAVE_FEEDBACK && !isFeatureSubmitStep) {
                    const informationalSteps = ['schedule_refresh', 'additional_recording', 'na_declined', 'voice_activity_detection'];
                    if (stepId !== 'add_veteran_modal_instructions') {
                      if (informationalSteps.includes(stepId)) {
                        foundEl.classList.add('tutorial-highlight-grey');
                      } else {
                        foundEl.classList.add('tutorial-highlight');
                        const clickableSteps = ['add_veteran', 'precharting_intro', 'veteran_consent', 'start_recording', 'stop_button', 'submit_recording', 'return_home', 'review_note', 'save_upload_note', 'manage_dot_phrase', 'telehealth_setup', 'telehealth_microphone_step', 'telehealth_screen_audio_step'];
                        if (clickableSteps.includes(stepId)) {
                          if (stepId === 'manage_dot_phrase') {
                            setTimeout(() => {
                              foundEl.classList.add('tutorial-highlight-flash');
                            }, 3000);
                          } else if (stepId === 'telehealth_setup' || stepId === 'telehealth_microphone_step' || stepId === 'telehealth_screen_audio_step') {
                            foundEl.classList.add('tutorial-highlight');
                          } else {
                            foundEl.classList.add('tutorial-highlight-flash');
                          }
                        }
                      }
                    }

                  if (stepId === 'leave_feedback') {
                    setTimeout(() => {
                      foundEl.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest'
                      });
                    }, 150);
                  }
                    foundEl.style.position = 'relative';
                    foundEl.style.zIndex = '10001';
                    if (sectionId === 'settings') {
                      foundEl.style.backgroundColor = 'white';
                    }
                  }
                  
                    if (stepId === 'leave_feedback') {
                      const leaveFeedbackElement = foundEl || document.querySelector(targetSelector);
                      if (leaveFeedbackElement) {
                        leaveFeedbackElement.classList.add('tutorial-highlight');
                        leaveFeedbackElement.style.position = 'relative';
                        leaveFeedbackElement.style.zIndex = '10001';
                      }
                    }

                  
                const spotlightPos = calculateSpotlightPosition(spotlightTarget(stepId, foundEl));
                setSpotlightPosition(spotlightPos);
                
                if (!isElementVisible(foundEl)) {
                  foundEl.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
                }
                
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    const newSpotlightPos = calculateSpotlightPosition(spotlightTarget(stepId, foundEl));
                    setSpotlightPosition(newSpotlightPos);
                  });
                });
                return;
              }
              
              if (additionalAttempts < maxAdditionalRetries) {
                additionalAttempts++;
                setTimeout(continueRetrying, additionalDelay);
              }
            };
            
            setTimeout(continueRetrying, additionalDelay);
            return;
          }
          
          if (stepId === 'telehealth_setup') {
            setTargetElement(null);
            setTooltipOpacity(1);
            return;
          }
          setTargetElement(null);
          setTooltipOpacity(1);
        return;
      }

        const element = foundElement;
        setTargetElement(element);
        previousTargetRef.current = element;

        if (!TOP_RIGHT_STEPS.includes(stepId) && stepId !== STEP_IDS.LEAVE_FEEDBACK && !isFeatureSubmitStep) {
          const informationalSteps = ['schedule_refresh', 'additional_recording', 'na_declined', 'voice_activity_detection'];
          if (stepId !== 'add_veteran_modal_instructions') {
            if (informationalSteps.includes(stepId)) {
              element.classList.add('tutorial-highlight-grey');
            } else {
              element.classList.add('tutorial-highlight');
              const clickableSteps = ['add_veteran', 'precharting_intro', 'veteran_consent', 'start_recording', 'stop_button', 'submit_recording', 'return_home', 'review_note', 'save_upload_note', 'manage_dot_phrase', 'telehealth_setup'];
              if (clickableSteps.includes(stepId)) {
                if (stepId === 'manage_dot_phrase') {
                  setTimeout(() => {
                    element.classList.add('tutorial-highlight-flash');
                  }, 3000);
                } else if (stepId === 'telehealth_setup' || stepId === 'telehealth_microphone_step' || stepId === 'telehealth_screen_audio_step') {
                  element.classList.add('tutorial-highlight');
                } else {
                  element.classList.add('tutorial-highlight-flash');
                }
              }
            }

            if (stepId === 'telehealth_setup') {
              const labelElement = element.closest('label');
              if (labelElement) {
                labelElement.classList.add('tutorial-highlight-flash');
                labelElement.style.position = 'relative';
                labelElement.style.zIndex = '10001';
              }
            }

            if (stepId === 'telehealth_microphone_step' || stepId === 'telehealth_screen_audio_step') {
              element.classList.add('tutorial-highlight');
              element.style.position = 'relative';
              element.style.zIndex = '10001';
            }
          }

          element.style.position = 'relative';
          element.style.zIndex = '10001';
          if (sectionId === 'settings') {
            element.style.backgroundColor = 'white';
          }
        }
        
        if (stepId === 'leave_feedback') {
          const leaveFeedbackElement = document.querySelector(targetSelector);
          if (leaveFeedbackElement) {
            leaveFeedbackElement.classList.add('tutorial-highlight');
            leaveFeedbackElement.style.position = 'relative';
            leaveFeedbackElement.style.zIndex = '10001';
          }
        }

        const spotlightPos = calculateSpotlightPosition(spotlightTarget(stepId, element));
        setSpotlightPosition(spotlightPos);
      
        if (!isElementVisible(element)) {
          if (stepId === 'save_upload_note' || stepId === 'review_note' || stepId === 'veteran_consent' || stepId === 'locked_note') {
            element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
          } else {
            element.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
          }
        }
        
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const rectAfterScroll = element.getBoundingClientRect();
            const newSpotlightPos = calculateSpotlightPosition(spotlightTarget(stepId, element));
            setSpotlightPosition(newSpotlightPos);
          });
        });
        
        if (spotlightUpdateRef.current) {
          clearInterval(spotlightUpdateRef.current);
        }
        
        if (!TOP_RIGHT_STEPS.includes(stepId) && targetSelector) {
          let lastSpotlightPos = null;
          spotlightUpdateRef.current = setInterval(() => {
            if (element && document.contains(element)) {
              const newSpotlightPos = calculateSpotlightPosition(spotlightTarget(stepId, element));
              
              if (!lastSpotlightPos || 
                  lastSpotlightPos.top !== newSpotlightPos.top ||
                  lastSpotlightPos.left !== newSpotlightPos.left ||
                  lastSpotlightPos.width !== newSpotlightPos.width ||
                  lastSpotlightPos.height !== newSpotlightPos.height) {
                setSpotlightPosition(newSpotlightPos);
                lastSpotlightPos = newSpotlightPos;
            }
          } else {
            if (spotlightUpdateRef.current) {
              clearInterval(spotlightUpdateRef.current);
              spotlightUpdateRef.current = null;
            }
          }
        }, (POSITIONING && POSITIONING.UPDATE_INTERVAL) ? POSITIONING.UPDATE_INTERVAL : 100);
      }
      
      const actionType = typeof action === 'string' ? action : action?.type;
      
      if (stepId === 'save_upload_note' && element) {
        const modal = element.closest('.modal') || document.querySelector('.modal.show');
        
        const handleButtonClick = (e) => {
          setTooltipOpacity(0);
        };
        
        if (modal) {
          // Advance the moment the modal STARTS closing (hide.bs.modal), not
          // after the fade-out finishes (hidden.bs.modal), so the Locked Note
          // slide appears immediately with no delay between slides. The
          // #review_note button persists (Review -> Locked), so it's found at
          // once. hidden.bs.modal stays as a fallback.
          let advanced = false;
          const advanceToLockedNote = () => {
            if (advanced) return;
            advanced = true;
            navigate('/dashboard');
            nextStep();
            modal.removeEventListener('hide.bs.modal', handleModalHide);
            modal.removeEventListener('hidden.bs.modal', handleModalHidden);
          };
          const handleModalHide = () => {
            setTooltipOpacity(0);
            advanceToLockedNote();
          };

          const handleModalHidden = () => {
            advanceToLockedNote();
          };

          modal.addEventListener('hide.bs.modal', handleModalHide);
          modal.addEventListener('hidden.bs.modal', handleModalHidden);
          
          element.addEventListener('click', handleButtonClick, true);
          
          clickHandlerRef.current = {
            hide: handleModalHide,
            hidden: handleModalHidden,
            buttonClick: handleButtonClick,
            modal: modal,
            element: element
          };
        } else {
          element.addEventListener('click', handleButtonClick, true);
          clickHandlerRef.current = {
            buttonClick: handleButtonClick,
            element: element
          };
        }
      } else if (stepId === 'leave_feedback' && element) {
        const handleButtonClick = () => {
          setTooltipOpacity(0);
        };

        element.addEventListener('click', handleButtonClick, true);
        clickHandlerRef.current = {
          buttonClick: handleButtonClick,
          element: element
        };
      } else if ((actionType === 'click' || stepId === 'add_veteran' || stepId === 'precharting_intro' || stepId === 'start_recording' || stepId === 'stop_button' || stepId === 'submit_recording' || stepId === 'return_home' || stepId === 'review_note' || stepId === 'manage_dot_phrase') && element && !action?.autoClick && !isFeatureSubmitStep) {
        clickHandlerRef.current = (e) => {
          if (stepId === 'start_recording') {
          element.style.transform = 'scale(0.95)';
          setTimeout(() => {
            element.style.transform = '';
              setTimeout(() => {
            nextStep();
              }, 300);
          }, 200);
          } else if (stepId === 'stop_button') {
            element.style.transform = 'scale(0.95)';
            setTimeout(() => {
              element.style.transform = '';
              setTimeout(() => {
                nextStep();
              }, 1000);
            }, 200);
          } else if (stepId === 'submit_recording') {
            element.style.transform = 'scale(0.95)';
            setTimeout(() => {
              element.style.transform = '';
              setTimeout(() => {
                nextStep();
              }, 500);
            }, 200);
          } else if (stepId === 'return_home') {
            element.style.transform = 'scale(0.95)';
            setTimeout(() => {
              element.style.transform = '';
              setTimeout(() => {
                nextStep();
              }, 300);
            }, 200);
          } else if (stepId === 'review_note') {
            if (reviewNoteAdvancedRef.current) return;
            reviewNoteAdvancedRef.current = true;
            element.style.transform = 'scale(0.95)';
            setTimeout(() => {
              element.style.transform = '';
              setTimeout(() => {
                nextStep();
              }, 800);
            }, 200);
          } else if (stepId === 'manage_dot_phrase') {
            element.style.transform = 'scale(0.95)';
            setTimeout(() => {
              element.style.transform = '';
              setTimeout(() => {
                nextStep();
              }, 500);
            }, 200);
          } else {
          element.style.transform = 'scale(0.95)';
          setTimeout(() => {
            element.style.transform = '';
            nextStep();
          }, 200);
          }
        };
        
        const useCapture = stepId === 'review_note';
        element.addEventListener('click', clickHandlerRef.current, useCapture);
      }
      
      requestAnimationFrame(() => {
        if (isMounted) {
          setTooltipOpacity(1);
        }
      });
      });
    };
    
    startElementSearch();

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
      if (spotlightUpdateRef.current) {
        clearInterval(spotlightUpdateRef.current);
        spotlightUpdateRef.current = null;
      }
      if (previousTargetRef.current) {
        if (!TOP_RIGHT_STEPS.includes(stepId)) {
          previousTargetRef.current.classList.remove('tutorial-highlight', 'tutorial-highlight-flash', 'tutorial-highlight-grey');
          previousTargetRef.current.style.zIndex = '';
          previousTargetRef.current.style.position = '';
        }
      }
      
      if (stepId !== 'leave_feedback') {
        const leaveFeedbackElements = document.querySelectorAll('[data-tutorial="leave_feedback"]');
        leaveFeedbackElements.forEach((leaveFeedbackElement) => {
          leaveFeedbackElement.classList.remove('tutorial-highlight', 'tutorial-highlight-flash', 'tutorial-highlight-grey');
          leaveFeedbackElement.style.zIndex = '';
          leaveFeedbackElement.style.position = '';
        });
        if (clickHandlerRef.current) {
          if (stepId === 'save_upload_note' && typeof clickHandlerRef.current === 'object') {
            const { hide, hidden, buttonClick, modal, element } = clickHandlerRef.current;
            if (modal) {
              modal.removeEventListener('hide.bs.modal', hide);
              modal.removeEventListener('hidden.bs.modal', hidden);
            }
            if (buttonClick && element) {
              element.removeEventListener('click', buttonClick, true);
            }
          } else if (stepId !== 'save_upload_note' && previousTargetRef.current) {
            const useCapture = stepId === 'review_note';
            previousTargetRef.current.removeEventListener('click', clickHandlerRef.current, useCapture);
          }
          clickHandlerRef.current = null;
        }
      }
      const allHighlightedElements = document.querySelectorAll('.tutorial-highlight');
      allHighlightedElements.forEach((el) => {
        el.classList.remove('tutorial-highlight');
        el.classList.remove('tutorial-highlight-flash');
        el.style.zIndex = '';
        el.style.position = '';
      });
      
      if (clickBlockerRef.current) {
        document.removeEventListener('click', clickBlockerRef.current, true);
        clickBlockerRef.current = null;
      }
      
      setSpotlightPosition(null);
    };
  }, [targetSelector, action, nextStep, stepId, position, sectionId]);

  useEffect(() => {
    if (stepId !== 'voice_activity_detection') {
      return;
    }

    const volumeValues = [25, 50, 75, 100, 60, 40, 80, 30, 70, 45, 90, 35];
    let volumeIndex = 0;
    let updatesElapsed = 0;

    const volumeMeterElement = document.querySelector('#voice_activity_detection');
    if (!volumeMeterElement) {
      return;
    }

    const volumeLevelElement = volumeMeterElement.children[0];
    if (!volumeLevelElement || volumeLevelElement.tagName !== 'DIV') {
      return;
    }

    const animationInterval = setInterval(() => {
      volumeIndex = updatesElapsed % volumeValues.length;
      const volumeValue = volumeValues[volumeIndex];
      
      volumeLevelElement.style.width = `${volumeValue}%`;
      
      updatesElapsed += 1;
    }, 333);

    volumeIndex = 0;
    volumeLevelElement.style.width = `${volumeValues[0]}%`;

    return () => {
      clearInterval(animationInterval);
      if (volumeLevelElement) {
        volumeLevelElement.style.width = '0%';
      }
    };
  }, [stepId]);

  const modalStepsWithoutBackdrop = [
    'telehealth_microphone_step',
    'telehealth_screen_audio_step',
    'telehealth_close_modal',
  ];
  // The Review & Upload slides run inside the note modal, which the backdrop
  // would cover — except these. The first three run on the dashboard; the tab
  // slides dim the screen around the tab they point at.
  const reviewStepsWithBackdrop = [
    'await_note_ready',
    'review_note',
    'locked_note',
    'leave_feedback',
    'save_upload_note',
    REVIEW_SMART_ASSIST_STEP_ID,
    ...Object.keys(TAB_ARIA_LABEL_BY_STEP),
  ];
  const shouldShowBackdrop = isActive &&
    (sectionId !== 'review_upload' || reviewStepsWithBackdrop.includes(stepId)) &&
    !modalStepsWithoutBackdrop.includes(stepId);
  // Steps anchored inside the Smart Chart window: the backdrop and the
  // spotlight shadow would otherwise stack to ~64% darkness. Use a single,
  // gentler dim so the window stays readable. The Smart Assist slides get the
  // same treatment — the panel is a fixed sidebar with its own stacking
  // context, so a tinted backdrop would dim the very thing they point at.
  const isSmartChartStep =
    stepId != null && stepId.startsWith('precharting_') && stepId !== 'precharting_intro';
  const usesGentleDim = isSmartChartStep || SMART_ASSIST_STEP_IDS.includes(stepId);
  const backdropDimColor =
    usesGentleDim || SPOTLIGHT_ONLY_DIM_STEPS.includes(stepId)
      ? 'rgba(0, 0, 0, 0)'
      : 'rgba(0, 0, 0, 0.4)';
  const isBackdropClickable = !NON_BLOCKING_BACKDROP_STEPS.includes(stepId);
  const spotlightDimColor = usesGentleDim ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.4)';

  useEffect(() => {
    if (!isActive) {
      if (globalClickBlockerRef.current) {
        document.removeEventListener('click', globalClickBlockerRef.current, true);
        globalClickBlockerRef.current = null;
      }
      releaseInteractionLock();
      return;
    }

    const blockedSteps = [
      'review_note',
      'review_note_page',
      'transcript_page',
      'note_citations',
      'patient_visit_summary_page',
      'leave_feedback'
    ];
    const allowMap = {
      add_veteran: '#add_veteran',
      precharting_intro: '#prechart_button',
      precharting_refetch: '#precharting_modal_dialog',
      precharting_overview: '#precharting_modal_dialog',
      precharting_allergies: '#precharting_modal_dialog',
      precharting_vitals: '#precharting_modal_dialog',
      precharting_additional_context: '#precharting_modal_dialog',
      precharting_custom_prompt: '#precharting_modal_dialog',
      precharting_optional_outline: '#precharting_modal_dialog',
      precharting_custom_instructions: '#precharting_modal_dialog',
      // Leaving this slide closes the Smart Chart window, so its close button
      // has to stay clickable even though the slide highlights the whole window.
      precharting_conversation_updates: '#precharting_modal_dialog, #precharting_modal_close',
      // Also allow the Smart Chart close button so the safety-net close works
      // if the window is somehow still open on this step.
      ready_to_record: '#ready_to_record, #precharting_modal_close',
      veteran_consent: '#ready_to_record',
      start_recording: '#start_recording',
      stop_button: '#stop_button',
      submit_recording: '#submit_recording',
      return_home: '#return_home',
      // The Import Dot Phrases slide points at the drop zone, so that panel —
      // not the whole Dot Phrases section — is what stays interactive.
      add_new_dot_phrase: '#upload_macros_panel',
      manage_dot_phrase: '#manage_dot_phrase_button button[role="tab"]:nth-of-type(2)',
      telehealth_setup: '#telehealth_setup_checkbox, #telehealth_setup_label',
      telehealth_microphone_step: '#telehealth_microphone_step',
      telehealth_screen_audio_step: '#telehealth_screen_audio_step',
      save_upload_note: '#save_upload_note',
      na_declined: '#na_declined_toggles',
    };

    // Anything a user can activate with a click. These only work when they are
    // part of the control the current slide highlights.
    const INTERACTIVE_SELECTOR = [
      'button',
      'a[href]',
      'select',
      'summary',
      'input[type="button"]',
      'input[type="submit"]',
      'input[type="reset"]',
      'input[type="checkbox"]',
      'input[type="radio"]',
      '[role="button"]',
      '[role="tab"]',
      '[role="link"]',
      '[role="menuitem"]',
      '[role="option"]',
      '[role="switch"]',
      '[data-bs-toggle]',
      '[data-bs-dismiss]',
    ].join(', ');
    // Selectors that cover a whole window instead of one control. The slide
    // leaves them interactive so the user can scroll and type inside them, but
    // the buttons they contain stay inert.
    const CONTAINER_ONLY_SELECTORS = [
      '#precharting_modal_dialog',
      '[data-testid="smart-assist-zoom-surface"]',
      '[data-testid="smart-assist-composer"]',
    ];
    const splitSelectors = (selector) =>
      (selector || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
    // Slides whose highlight is purely informational: the user should look at
    // it, not use it. The feedback stars are highlighted but must not be
    // rateable while the tutorial is walking through the note.
    const nonInteractiveHighlightSteps = ['leave_feedback'];
    const isNonInteractiveHighlight = nonInteractiveHighlightSteps.includes(stepId);
    const stepSelectors = [
      ...splitSelectors(targetSelector),
      ...splitSelectors(allowMap[stepId]),
    ];
    // The controls this slide points the user at: its highlighted element plus
    // any control it explicitly opens up.
    const highlightedControlSelectors = isNonInteractiveHighlight
      ? []
      : stepSelectors.filter((selector) => !CONTAINER_ONLY_SELECTORS.includes(selector));
    // The tutorial's own UI, anything wearing the clickable highlight, plus
    // provider attestation, which blocks dashboard access and so must stay
    // usable even mid-tutorial.
    const alwaysInteractiveSelectors = [
      '.tutorial-tooltip',
      // On an informational slide nothing is clickable, so a highlight class
      // left over from the previous slide must not re-open anything.
      ...(isNonInteractiveHighlight ? [] : CLICKABLE_HIGHLIGHT_SELECTORS),
      '[data-provider-attestation-modal="true"]',
      '[data-provider-attestation-dialog="true"]',
    ];

    // Blocking events only stops handlers that go through the document. Take
    // the controls out of hit-testing as well, so a button can't be activated
    // by any wiring — this is what keeps the note modal's action buttons
    // (Close, Save & Upload to EHR, Add a Recording, Save Changes)
    // inert while the tutorial walks through the note.
    const scopeToLock = (selector) => `body.${INTERACTION_LOCK_CLASS} ${selector}`;
    const interactiveSelectors = splitSelectors(INTERACTIVE_SELECTOR);
    // On an informational slide, lock the highlighted region itself as well —
    // whatever it contains (the feedback stars' buttons) goes inert with it.
    const informationalHighlightSelectors = isNonInteractiveHighlight
      ? stepSelectors.flatMap((selector) => [selector, `${selector} *`])
      : [];
    const lockedRules = [...interactiveSelectors, ...informationalHighlightSelectors]
      .map(scopeToLock)
      .join(',\n');
    // Only the controls are unlocked again, so decorative overlays that opt out
    // of pointer events inside these regions keep doing so.
    const unlockedRules = [...alwaysInteractiveSelectors, ...highlightedControlSelectors]
      .flatMap((selector) => [
        scopeToLock(selector),
        ...interactiveSelectors.map((control) => scopeToLock(`${selector} ${control}`)),
      ])
      .join(',\n');
    let lockStyleElement = document.getElementById(INTERACTION_LOCK_STYLE_ID);
    if (!lockStyleElement) {
      lockStyleElement = document.createElement('style');
      lockStyleElement.id = INTERACTION_LOCK_STYLE_ID;
      document.head.appendChild(lockStyleElement);
    }
    lockStyleElement.textContent =
      `${lockedRules} { pointer-events: none !important; }\n` +
      `${unlockedRules} { pointer-events: auto !important; }`;
    document.body.classList.add(INTERACTION_LOCK_CLASS);

    const isAllowedTarget = (target) => {
      if (tooltipRef.current && tooltipRef.current.contains(target)) {
        return true;
      }

      if (target?.closest?.('[class*="Modal-module"]')) {
        return true;
      }

      // Provider attestation must remain interactive even when the tutorial
      // is active, because it blocks dashboard access.
      if (
        Boolean(
          target?.closest?.('[data-provider-attestation-modal="true"], [data-provider-attestation-dialog="true"]')
        )
      ) {
        return true;
      }

      // Whatever the slide flags as clickable with the flashing highlight. Some
      // slides highlight an element their targetSelector doesn't name (the
      // review-note tabs, the settings "My Dot Phrases" tab).
      if (
        !isNonInteractiveHighlight &&
        target?.closest?.(CLICKABLE_HIGHLIGHT_SELECTORS.join(', '))
      ) {
        return true;
      }

      // The region of the page this slide leaves interactive at all.
      let regionSelector;
      if (isNonInteractiveHighlight) {
        regionSelector = null;
      } else if (blockedSteps.includes(stepId)) {
        // leave_feedback is covered by the informational branch above.
        regionSelector = stepId === 'review_note' ? '#review_note' : '#save_upload_note';
      } else {
        regionSelector = allowMap[stepId];
      }

      if (!regionSelector || !target?.closest?.(regionSelector)) {
        return false;
      }

      // Within that region, only the highlighted control is clickable — every
      // other button stays inert so no stray click can break the tutorial flow.
      const control = target?.closest?.(INTERACTIVE_SELECTOR);
      if (!control) {
        return true;
      }
      return highlightedControlSelectors.some((selector) => {
        try {
          return control.matches(selector) || Boolean(control.closest(selector));
        } catch (err) {
          return false;
        }
      });
    };

    const handleGlobalInteraction = (e) => {
      if (isAllowedTarget(e.target)) {
        return;
      }

      const isKeyboard = e.type === 'keydown' || e.type === 'keyup';
      if (isKeyboard) {
        const mod = e.metaKey || e.ctrlKey;
        const isZoom = mod && (e.key === '=' || e.key === '+' || e.key === '-' || e.key === '0');
        const isRefresh = mod && e.key === 'r';
        const isDevTools = mod && e.shiftKey && (e.key === 'I' || e.key === 'i');
        if (isZoom || isRefresh || isDevTools) return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    };

    const events = [
      'click',
      'dblclick',
      'pointerdown',
      'pointerup',
      'mousedown',
      'mouseup',
      'touchstart',
      'touchend',
      'keydown',
      'keyup',
      'submit',
      'pointerover',
      'mouseover'
    ];
    events.forEach((eventName) => {
      document.addEventListener(eventName, handleGlobalInteraction, true);
    });
    globalClickBlockerRef.current = handleGlobalInteraction;

    return () => {
      if (globalClickBlockerRef.current) {
        events.forEach((eventName) => {
          document.removeEventListener(eventName, globalClickBlockerRef.current, true);
        });
        globalClickBlockerRef.current = null;
      }
      releaseInteractionLock();
    };
  }, [isActive, stepId, targetSelector]);

  return createPortal(
    <>
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200 z-[10002]">
        <div 
          className="h-full bg-purple-600 transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Backdrop with spotlight */}
      {shouldShowBackdrop && (
        <div
          className="fixed inset-0 z-[10000] tutorial-backdrop-bg"
          style={{
            backgroundColor:
              stepId === 'add_veteran_modal_instructions'
                ? 'rgba(0, 0, 0, 0)'
                : backdropDimColor,
            pointerEvents: isBackdropClickable ? 'auto' : 'none',
          }}
          onClick={isBackdropClickable ? handleBackdropClick : undefined}
        >
          {stepId !== 'add_veteran_modal_instructions' && spotlightPosition && (
          <div 
              className="absolute"
            style={{
                top: `${spotlightPosition.top}px`,
                left: `${spotlightPosition.left}px`,
                width: `${spotlightPosition.width}px`,
                height: `${spotlightPosition.height}px`,
                boxShadow: `0 0 0 9999px ${spotlightDimColor}`,
                pointerEvents: 'none',
                borderRadius: '8px',
            }}
          />
        )}
      </div>
      )}

      {/* Tooltip */}
      <div 
        ref={setTooltipRef}
        role="dialog"
        aria-label={name ? `Tutorial: ${name}` : 'Tutorial'}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !showExitConfirmation) {
            e.stopPropagation();
            handleCloseAttempt();
          }
        }}
        className="tutorial-tooltip fixed transform rounded-2xl z-[10002] focus:outline-none"
        style={{
          // Centred until there is a laid-out target AND Floating UI has
          // measured it — styles are null on the first pass, and spreading
          // those would drop the card in the corner.
          ...(isCenteredStep || !hasPositionAnchor || !floatingStyles
            ? { position: 'fixed', top: '50%', left: '50%', transform: `translate(-50%, -50%) translateY(${tooltipOpacity === 1 ? 0 : -10}px)` }
            : { ...floatingStyles, transform: `translateY(${tooltipOpacity === 1 ? 0 : -10}px)` }
          ),
          // Every slide is the same width. The video slide is the exception:
          // it shows on its own, with nothing beside it to match.
          width: stepId === 'telehealth_close_modal' ? 'min(720px, 90vw)' : '400px',
          maxWidth: 'calc(100vw - 20px)',
          maxHeight: stepId === 'tutorial_complete'
            ? 'min(70vh, calc(100vh - 20px))'
            : 'calc(100vh - 20px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          opacity: tooltipOpacity,
          transition: 'opacity 200ms ease-out, transform 200ms ease-out',
          willChange: 'opacity, transform',
          // Matches the elevation @knowtex-ai/components gives a Modal.
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12), 0 8px 48px rgba(0, 0, 0, 0.08)',
        }}
      >
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-white">
          {/* Close button */}
          <Button
            variant="text"
            size="small"
            icon={Xmark}
            onClick={handleCloseAttempt}
            aria-label="Close tutorial"
            className="tutorial-close absolute right-4 top-4"
          />
          
          {/* Header */}
          <div className="tutorial-tooltip-scroll min-h-0 flex-1 overflow-y-auto text-left px-8 pt-8 pb-6">
            {/* Title */}
            {name && (
              <Heading as="h2" size="medium" className="mb-3">
                {name}
              </Heading>
            )}
          
          {/* Content */}
          <div className="mb-4">
              {(() => {
                try {
                  const isMockPatient = patientDescription === 'QQQVAINTERFACE,IFC';
                  const shouldHideInfo =
                    !isMockPatient &&
                    (stepId === 'voice_activity_detection' ||
                      stepId === 'speaking_freely');
                  const filteredContent = shouldHideInfo && Array.isArray(content)
                    ? content.filter((item) => item?.type !== 'info')
                    : content;

                  return renderContent(filteredContent, stepId);
                } catch (error) {
                  console.error('[Tutorial] Error rendering content for step:', stepId, error);
                  console.error('[Tutorial] Content:', content);
                  return <p className="text-gray-600">Content loading...</p>;
                }
              })()}
          </div>
              </div>
              
          {/* Footer */}
          <div className="border-t border-purple-100 bg-purple-50 bg-opacity-30 px-8 py-6">
            <div className="flex gap-3">
                {showFooterPrimaryButton && (
                  shouldShowFinishButton ? (
                    <Button variant="primary" fullWidth onClick={handleFinish}>
                      Finish
                    </Button>
                  ) : !isLastStep ? (
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={handleNext}
                      disabled={isNextDisabled}
                    >
                      {isNextDisabled ? 'Preparing note…' : 'Next Step'}
                    </Button>
                  ) : stepId === 'add_dot_phrase_in_note' ? (
                    <Button variant="primary" fullWidth onClick={handleNext}>
                      Next
                    </Button>
                  ) : (
                    <Button variant="primary" fullWidth onClick={handleFinish}>
                      Finish
                    </Button>
                  ))}
              </div>
              {/* Slide counter */}
              {totalSteps > 0 && (
                <div className="text-center text-gray-400 mt-2.5 text-xs">
                  {Math.min((stepIndex ?? 0) + 1, totalSteps)} / {totalSteps}
                </div>
              )}
            </div>
        </div>
      </div>
      <ConfirmationModal
        open={showExitConfirmation}
        title="Exit tutorial?"
        continueText="Exit"
        cancelText="Keep going"
        continueVariant="primary-danger"
        onContinue={() => {
          setShowExitConfirmation(false);
          doExit();
        }}
        onCancel={() => setShowExitConfirmation(false)}
      >
        <p>Your progress will be lost and you'll need to start the tutorial from the beginning.</p>
      </ConfirmationModal>
    </>,
    document.body
  );
};

export default TutorialTooltip;